import { useNavigate } from '@tanstack/react-router';
import type { ListMutualDuosResponseUser } from 'generated/openapi';
import { memo, useCallback } from 'react';

import defaultAvatar from '@/assets/img/default-avatar.png';
import { useListMutualDuos } from '@/features/profile/hooks/useListMutualDuos';
import { OnlineIndicator } from '@/features/users/components/onlineIndicator';
import { DisplayName } from '@/shared/components/displayName';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/shared/components/ui/avatar';
import { RarityCard } from '@/shared/components/ui/rarityCard';
import { cn } from '@/shared/utils/cn';
import { rarityFitLabels, rarityTextColors } from '@/shared/utils/rarity';

interface MutualDuosGridProps {
  userId: string;
}

export const MutualDuosGrid = memo(function MutualDuosGrid({
  userId,
}: MutualDuosGridProps) {
  const { data } = useListMutualDuos({ userId });
  const mutualDuos = data.content;

  if (mutualDuos.length === 0) {
    return (
      <div className="text-cold-400 flex items-center justify-center py-8 text-sm">
        No mutual duos yet
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 justify-items-center gap-x-6 gap-y-5">
      {mutualDuos.map(user => (
        <MutualDuoCard key={user.userId} user={user} />
      ))}
    </div>
  );
});

interface MutualDuoCardProps {
  user: ListMutualDuosResponseUser;
}

const MutualDuoCard = memo(function MutualDuoCard({
  user,
}: MutualDuoCardProps) {
  const { pictureUrl, displayName, onlineStatus, rarity, overallScore } = user;
  const navigate = useNavigate();

  const textColor = rarityTextColors[rarity];
  const scorePercent =
    overallScore !== undefined ? Math.round(overallScore * 100) : undefined;

  const handleClick = useCallback(() => {
    void navigate({
      to: '/profiles/$profileId',
      params: { profileId: user.userId },
    });
  }, [navigate, user.userId]);

  return (
    <RarityCard.Outer
      rarity={rarity}
      size="small"
      className="h-35 w-26 cursor-pointer rounded-2xl"
      onClick={handleClick}
    >
      <RarityCard.Inner className="relative flex h-full flex-col items-center gap-2 rounded-xl px-1 pt-2 pb-1">
        <div className="flex w-full items-center justify-center gap-1">
          {onlineStatus === 'ONLINE' && <OnlineIndicator />}
          <DisplayName
            name={displayName}
            isPremium={user.isPremium}
            className="text-sm font-semibold"
            badgePosition="after"
          />
        </div>
        <div className="container-size relative flex min-h-0 w-full flex-1 items-center justify-center">
          <div className="square-fit relative">
            <Avatar className="h-full w-full">
              <AvatarFallback>
                <img
                  src={defaultAvatar}
                  alt="Default avatar"
                  className="size-full object-cover"
                />
              </AvatarFallback>
              <AvatarImage
                src={pictureUrl}
                alt={displayName}
                className="h-full w-full object-cover"
              />
            </Avatar>
          </div>
        </div>
        <p
          className={cn(
            'font-saira text-center text-xs leading-[120%] font-bold',
            textColor,
          )}
        >
          {rarityFitLabels[rarity]}
          {scorePercent !== undefined && ` ${scorePercent.toString()}%`}
        </p>
      </RarityCard.Inner>
    </RarityCard.Outer>
  );
});
